import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeftIcon,
  EyeIcon,
  TrophyIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { LeagueService, TeamService, ContestantService, PredictionService, SeasonService } from '../../../services/firebaseService';
import {
  isDoubleLikeEliminationWeek,
  isMultiEliminationWeek,
  isSimultaneousDoubleEliminationWeek,
} from '../../../utils/predictionUtils';

export default function LeaguePredictions() {
  const { leagueId } = useParams();
  const [searchParams] = useSearchParams();
  const [league, setLeague] = useState(null);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [teams, setTeams] = useState([]);
  const [predictions, setPredictions] = useState({});
  const [contestants, setContestants] = useState([]);
  const [seasonData, setSeasonData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId) {
      loadLeagueData();
    }
  }, [leagueId, searchParams]);

  useEffect(() => {
    if (selectedSeason) {
      loadSeasonData();
    }
  }, [selectedSeason]);

  const loadLeagueData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const leagueData = await LeagueService.getLeague(leagueId);
      if (!leagueData) {
        throw new Error('League not found');
      }
      
      setLeague(leagueData);
      
      // Load seasons for this league
      const seasonsData = await LeagueService.getLeagueSeasons(leagueId);
      setSeasons(seasonsData);
      
      // Check for season parameter in URL
      const seasonParam = searchParams.get('season');
      if (seasonParam && seasonsData.length > 0) {
        // Find the season that matches the parameter
        const matchingSeason = seasonsData.find(season => season.seasonId === seasonParam);
        if (matchingSeason) {
          setSelectedSeason(matchingSeason);
        } else {
          // If season parameter doesn't match any season, use the first one
          setSelectedSeason(seasonsData[0]);
        }
      } else if (seasonsData.length > 0) {
        // Auto-select the first season if no season parameter
        setSelectedSeason(seasonsData[0]);
      }
      
    } catch (error) {
      console.error('Error loading league data:', error);
      setError(error.message || 'Failed to load league data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSeasonData = async () => {
    try {
      setLoading(true);
      
      // Load teams for the selected season
      const teamsData = await TeamService.getTeams(leagueId, selectedSeason.seasonId);
      setTeams(teamsData);
      
      // Load contestant details properly
      let contestantDetails = [];
      
      try {
        // Always try to load contestants by season ID to get full contestant objects
        contestantDetails = await ContestantService.getContestantsBySeason(selectedSeason.seasonId);
      } catch (contestantError) {
        console.warn('Could not load contestants by season ID:', contestantError);
        
        // Fallback: if league season has contestant IDs, try to load them individually
        if (selectedSeason.contestants && selectedSeason.contestants.length > 0) {
          try {
            const individualContestants = await Promise.all(
              selectedSeason.contestants.map(async (contestantId) => {
                try {
                  return await ContestantService.getContestant(contestantId);
                } catch (error) {
                  console.warn(`Could not load contestant ${contestantId}:`, error);
                  return null;
                }
              })
            );
            contestantDetails = individualContestants.filter(contestant => contestant !== null);
          } catch (individualError) {
            console.warn('Could not load individual contestants:', individualError);
          }
        }
      }
      
      setContestants(contestantDetails);
      
      // Load season data to get weeks with eliminationType
      try {
        const fullSeasonData = await SeasonService.getSeason(selectedSeason.seasonId);
        setSeasonData(fullSeasonData);
      } catch (seasonError) {
        console.warn('Could not load full season data:', seasonError);
      }
      
      // Try to load predictions, but handle permission errors gracefully
      const predictionsData = {};
      
      // Initialize predictions data structure for all teams
      teamsData.forEach(team => {
        predictionsData[team.id] = [];
      });
      
      // Try to load predictions for each team individually
      // This approach might work better for league managers
      let hasPermission = false;
      let permissionError = null;
      
      // Test permissions by trying to get a prediction for the first team
      if (teamsData.length > 0) {
        try {
          const testPrediction = await Promise.race([
            PredictionService.getTeamPrediction(leagueId, selectedSeason.seasonId, teamsData[0].id, 1),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10000)) // 10 second timeout
          ]);
          hasPermission = true;
        } catch (error) {
          console.warn('Permission denied or timeout for predictions collection:', error);
          hasPermission = false;
          permissionError = error;
        }
      }
      
      if (hasPermission) {
        // Single query to get ALL predictions for this league and season (much more efficient)
        try {
          const allPredictions = await Promise.race([
            PredictionService.getAllLeaguePredictions(leagueId, selectedSeason.seasonId),
            new Promise((_, reject) => setTimeout(() => reject(new Error('Timeout')), 15000)) // 15 second timeout
          ]);
          
          // Group predictions by team
          allPredictions.forEach(prediction => {
            if (predictionsData[prediction.teamId]) {
              predictionsData[prediction.teamId].push(prediction);
            } else {
              console.warn('No team data found for teamId:', prediction.teamId, 'Available team IDs:', Object.keys(predictionsData));
            }
          });
        } catch (predictionError) {
          console.warn('Could not load predictions:', predictionError);
          setError('Failed to load predictions. Please try again.');
        }
      } else {
        // Set a flag to show that predictions are not available
        setError('Predictions data is not available due to permission restrictions. This is likely because the Firebase security rules need to be updated to allow league managers to read predictions for their own leagues.');
      }
      
      setPredictions(predictionsData);
      
    } catch (error) {
      console.error('Error loading season data:', error);
      setError('Failed to load season data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getContestantName = (contestantId) => {
    if (!contestantId) return 'N/A';
    
    // If contestants is an array of objects with id and name
    if (Array.isArray(contestants) && contestants.length > 0) {
      const contestant = contestants.find(c => c && c.id === contestantId);
      if (contestant && contestant.name) {
        return contestant.name;
      }
    }
    
    // Fallback: show the ID
    return `ID: ${contestantId}`;
  };

  const formatPrediction = (prediction, weekNumber) => {
    if (!prediction) return 'N/A';
    
    // Get week data to check eliminationType
    const week = seasonData?.weeks?.find(w => w.weekNumber === weekNumber);
    const isMultiElim = isMultiEliminationWeek(week);
    const isFinale = week?.isFinale || weekNumber === 13;
    
    // Handle finale week
    if (isFinale && prediction.winner) {
      return `V: ${getContestantName(prediction.votedOut)}, F: ${getContestantName(prediction.fireMakingLoser)}, W: ${getContestantName(prediction.winner)}`;
    }
    
    // Handle double / triple elimination weeks
    if (isMultiElim) {
      const predictions = [];
      
      // Check for votedOut1 and votedOut2 properties (old format)
      if (prediction.votedOut1) {
        const name1 = getContestantName(prediction.votedOut1);
        if (name1 && name1 !== 'N/A') {
          predictions.push(name1);
        }
      }
      if (prediction.votedOut2) {
        const name2 = getContestantName(prediction.votedOut2);
        if (name2 && name2 !== 'N/A') {
          predictions.push(name2);
        }
      }
      
      // Check for votedOut array format (new format)
      if (prediction.votedOut) {
        if (Array.isArray(prediction.votedOut)) {
          // Check if array contains objects (new format) or strings (old format)
          if (prediction.votedOut.length > 0 && typeof prediction.votedOut[0] === 'object' && prediction.votedOut[0] !== null) {
            // Array of objects with contestantId and position
            // Don't filter out items with null contestantId yet - we want to see all items for debugging
            const sorted = prediction.votedOut
              .filter(item => item !== null && item !== undefined) // Only filter out completely null items
              .sort((a, b) => (a.position || 0) - (b.position || 0));
            // For double elimination, we want to show both positions even if one is null
            // Map all items (including nulls) to show the structure
            const allNames = sorted.map((item, index) => {
              if (item.contestantId) {
                return getContestantName(item.contestantId);
              }
              return null; // Keep null to maintain position structure
            }).filter(name => name !== null && name !== 'N/A'); // Filter out nulls and N/A at the end
            
            if (allNames.length > 0) {
              // If we already have predictions from votedOut1/votedOut2, merge them
              // Otherwise, use the array format
              if (predictions.length === 0) {
                predictions.push(...allNames);
              }
            }
          } else {
            // Array of strings (contestant IDs)
            const arrayNames = prediction.votedOut
              .filter(id => id) // Filter out null/undefined
              .map(id => getContestantName(id))
              .filter(name => name && name !== 'N/A');
            if (arrayNames.length > 0 && predictions.length === 0) {
              predictions.push(...arrayNames);
            }
          }
        } else if (typeof prediction.votedOut === 'string') {
          // Single string value (fallback for single elimination format in double week)
          const name = getContestantName(prediction.votedOut);
          if (name && name !== 'N/A' && predictions.length === 0) {
            predictions.push(name);
          }
        }
      }
      
      return predictions.length > 0 ? predictions.join(', ') : 'N/A';
    }
    
    // Handle single elimination weeks
    if (prediction.votedOut) {
      if (Array.isArray(prediction.votedOut)) {
        // Check if array contains objects (new format) or strings (old format)
        if (prediction.votedOut.length > 0 && typeof prediction.votedOut[0] === 'object' && prediction.votedOut[0] !== null) {
          // Array of objects with contestantId and position
          const sorted = prediction.votedOut
            .filter(item => item && item.contestantId) // Filter out null/undefined items
            .sort((a, b) => (a.position || 0) - (b.position || 0));
          return sorted.map(item => getContestantName(item.contestantId)).join(', ');
        } else {
          // Array of strings (contestant IDs)
          return prediction.votedOut
            .filter(id => id) // Filter out null/undefined
            .map(id => getContestantName(id))
            .join(', ');
        }
      }
      // Single string value
      return getContestantName(prediction.votedOut);
    }
    
    return 'N/A';
  };

  const getWeekHeaders = () => {
    if (!selectedSeason) return [];
    
    // Use actual week data if available, otherwise default to 13 weeks
    if (seasonData?.weeks && seasonData.weeks.length > 0) {
      return seasonData.weeks
        .sort((a, b) => a.weekNumber - b.weekNumber)
        .map(week => ({
          number: week.weekNumber,
          label: week.isFinale
            ? `Week ${week.weekNumber} (Finale)`
            : week.eliminationType === 'triple'
              ? `Week ${week.weekNumber} (Triple)`
              : isDoubleLikeEliminationWeek(week)
                ? `Week ${week.weekNumber} (${
                    isSimultaneousDoubleEliminationWeek(week) ? 'Double sim.' : 'Double'
                  })`
                : `Week ${week.weekNumber}`
        }));
    }
    
    // Fallback: create default weeks
    const weeks = [];
    for (let i = 1; i <= 13; i++) {
      weeks.push({
        number: i,
        label: i === 7 ? 'Week 7 (Double)' : i === 13 ? 'Week 13 (Finale)' : `Week ${i}`
      });
    }
    return weeks;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading predictions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Predictions Not Available</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          
          {error.includes('permission restrictions') && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 text-left">
              <h3 className="text-sm font-medium text-blue-800 mb-2">Temporary Workaround:</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• <strong>Use Admin Route:</strong> <a href={`/admin/leagues/${leagueId}/predictions`} className="text-blue-600 hover:text-blue-800 underline">Click here to view predictions via admin route</a></li>
                <li>• <strong>View Individual Teams:</strong> Go to Teams Management to see individual team predictions</li>
                <li>• <strong>Contact Admin:</strong> Request Firebase security rules to be updated for league managers</li>
              </ul>
            </div>
          )}
          
          <div className="space-x-4">
            <button
              onClick={loadLeagueData}
              className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
            >
              Try Again
            </button>
            <Link
              to={`/manage-leagues/${leagueId}`}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Back to League
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!league || !selectedSeason) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <TrophyIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Season Selected</h2>
          <p className="text-gray-600 mb-4">Please select a season to view predictions.</p>
          <Link
            to={`/manage-leagues/${leagueId}`}
            className="px-4 py-2 bg-survivor-600 text-white rounded-md hover:bg-survivor-700"
          >
            Back to League
          </Link>
        </div>
      </div>
    );
  }

  const weekHeaders = getWeekHeaders();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link
                to={`/manage-leagues/${leagueId}`}
                className="mr-4 p-2 text-gray-400 hover:text-gray-600"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </Link>
              <div className="flex items-center">
                <EyeIcon className="h-8 w-8 text-gray-400 mr-3" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">League Predictions</h1>
                  <p className="mt-1 text-sm text-gray-500">
                    {league.name} - {selectedSeason.name}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Season Selector */}
            <div className="flex items-center space-x-4">
              <label htmlFor="season-select" className="text-sm font-medium text-gray-700">
                Season:
              </label>
              <select
                id="season-select"
                value={selectedSeason?.id || ''}
                onChange={(e) => {
                  const season = seasons.find(s => s.id === e.target.value);
                  setSelectedSeason(season);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
              >
                {seasons.map(season => (
                  <option key={season.id} value={season.id}>
                    {season.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Predictions Grid */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10">
                      Team
                    </th>
                    {weekHeaders.map(week => (
                      <th key={week.number} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px]">
                        {week.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teams.map(team => (
                    <tr key={team.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-0 bg-white z-10 border-r border-gray-200">
                        {team.name}
                      </td>
                      {weekHeaders.map(week => {
                        const teamPrediction = predictions[team.id]?.find(p => p.weekNumber === week.number);
                        const predictionText = teamPrediction ? formatPrediction(teamPrediction.predictions, week.number) : 'N/A';
                        const isNA = predictionText === 'N/A';
                        
                        return (
                          <td key={week.number} className={`px-3 py-4 text-sm text-center ${isNA ? 'text-gray-400' : 'text-gray-900'}`}>
                            <div className="max-w-[120px] truncate" title={predictionText}>
                              {predictionText}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="px-4 py-6 sm:px-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrophyIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Teams
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {teams.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <EyeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Total Predictions
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {Object.values(predictions).flat().length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <TrophyIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">
                        Contestants
                      </dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {contestants.length}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
